"use client";
import { useState } from "react";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { routeAfterAuth } from "@/lib/postAuth";

export type AccountArea = "personal" | "instructor";

// Hem eğitmen hem kişisel hesabı olan kişinin iki alan arasında çıkış yapmadan
// geçmesi için iki sekmeli düğme. Hesap tektir (aynı token); geçiş yalnızca hangi
// alanın açılacağını değiştirir. Kimin göreceğine çağıran karar verir: öğrenci
// kabuğu eğitmen rolü olana, eğitmen kabuğu kişisel hesabı da kullanılmış olana.
//
// Kişisel alana geçiş doğrudan /dashboard'a değil normal giriş sırasına gider:
// e-posta doğrulaması ya da güncel hukuki onay eksikse önce o ekran gelir.
export function AccountSwitcher({ current }: { current: AccountArea }) {
  const t = useTranslations("common.accountSwitch");
  const router = useRouter();
  const [busy, setBusy] = useState(false);

  async function go(target: AccountArea) {
    if (target === current || busy) return;
    setBusy(true);
    if (target === "instructor") router.push("/instructor");
    else await routeAfterAuth(router);
  }

  const tabs: { key: AccountArea; label: string; hint: string }[] = [
    { key: "personal", label: t("personal"), hint: t("toPersonal") },
    { key: "instructor", label: t("instructor"), hint: t("toInstructor") },
  ];

  return (
    <div
      role="group"
      aria-label={t("label")}
      className="inline-flex items-center rounded-full border border-[var(--border)] bg-[var(--surface)] p-0.5 shadow-sm"
    >
      {tabs.map(tab => {
        const active = tab.key === current;
        return (
          <button
            key={tab.key}
            type="button"
            onClick={() => go(tab.key)}
            aria-pressed={active}
            title={active ? undefined : tab.hint}
            disabled={busy && !active}
            className={`rounded-full px-3 py-1 text-xs font-medium whitespace-nowrap transition-colors ${
              active
                ? "text-white cursor-default"
                : "text-[var(--text-secondary)] hover:text-[var(--text-primary)] disabled:opacity-60"
            }`}
            style={active ? { background: "var(--accent)" } : undefined}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}
